import {
  researchDetailedAnalysisResponseSchema,
  researchFeedbackInputSchema,
  researchFeedbackResponseSchema,
  researchReelListResponseSchema,
  type ResearchDetailedAnalysis,
  type ResearchFeedback,
  type ResearchReel,
  type ResearchReelListResponse,
} from "@reelio/contracts";

export type {
  ResearchDetailedAnalysis,
  ResearchFeedback,
  ResearchReel,
  ResearchReelListResponse,
};

type ErrorPayload = {
  error?: {
    code?: string;
    message?: string;
  };
  message?: string;
};

export class ResearchApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, status: number, code = "RESEARCH_API_ERROR") {
    super(message);
    this.name = "ResearchApiError";
    this.code = code;
    this.status = status;
  }
}

const readJson = async <T>(response: Response): Promise<T> => {
  const payload = (await response.json().catch(() => null)) as
    | ErrorPayload
    | T
    | null;

  if (!response.ok) {
    const errorPayload = payload as ErrorPayload | null;
    throw new ResearchApiError(
      errorPayload?.error?.message ??
        errorPayload?.message ??
        "Instagram research is temporarily unavailable.",
      response.status,
      errorPayload?.error?.code,
    );
  }

  if (payload === null) {
    throw new ResearchApiError(
      "Instagram research returned an empty response.",
      response.status,
      "INVALID_RESEARCH_RESPONSE",
    );
  }

  return payload as T;
};

const unwrapData = <T>(payload: T | { data: T }): T =>
  typeof payload === "object" && payload !== null && "data" in payload
    ? payload.data
    : payload;

export const getResearchReels = async (
  signal?: AbortSignal,
): Promise<ResearchReelListResponse> => {
  const response = await fetch("/api/research/reels", {
    credentials: "include",
    headers: { Accept: "application/json" },
    signal,
  });
  const payload = await readJson<
    ResearchReelListResponse | { data: ResearchReelListResponse }
  >(
    response,
  );
  return researchReelListResponseSchema.parse(unwrapData(payload));
};

export const saveReelFeedback = async (
  reelId: string,
  feedback: ResearchFeedback,
): Promise<ResearchFeedback> => {
  const input = researchFeedbackInputSchema.parse({ feedback });
  const response = await fetch(
    `/api/research/reels/${encodeURIComponent(reelId)}/feedback`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const payload = await readJson<
    | { reelId: string; feedback: ResearchFeedback }
    | { data: { reelId: string; feedback: ResearchFeedback } }
  >(response);
  return researchFeedbackResponseSchema.parse(unwrapData(payload)).feedback;
};

export const generateReelAnalysis = async (
  reelId: string,
): Promise<ResearchDetailedAnalysis> => {
  const response = await fetch(
    `/api/research/reels/${encodeURIComponent(reelId)}/analysis`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const payload = await readJson<
    | {
        reelId: string;
        analysis: ResearchDetailedAnalysis;
      }
    | {
        data: {
          reelId: string;
          analysis: ResearchDetailedAnalysis;
        };
      }
  >(response);
  return researchDetailedAnalysisResponseSchema.parse(unwrapData(payload))
    .analysis;
};
